import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  getDocs,
  getDoc,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  MembershipDocument,
  LeadMember,
  AddMemberRequest,
  RemoveMemberRequest,
  MemberSearchResult,
  RecentUser
} from '../types/Member';
import { User } from '../types/User';
import { COLLECTION_NAMES } from '../utils/constants';

export class MemberService {
  private static instance: MemberService;
  private membersCache = new Map<string, LeadMember[]>(); // leadId -> members
  private recentUsersCache: RecentUser[] | null = null;
  private lastRecentUsersFetch: number = 0;
  private allLeadsWithMembersCache: string[] | null = null;
  private allLeadsWithMembersFetchedAt: number = 0;
  private leadsForMemberCache = new Map<string, { data: string[]; fetchedAt: number }>();
  private readonly CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

  private constructor() {}

  static getInstance(): MemberService {
    if (!MemberService.instance) {
      MemberService.instance = new MemberService();
    }
    return MemberService.instance;
  }

  /**
   * Add a member to a lead (reuses existing records if available)
   */
  async addMemberToLead(request: AddMemberRequest): Promise<LeadMember> {
    try {
      // First, check if a membership record already exists (active or removed)
      const membersRef = collection(db, COLLECTION_NAMES.MEMBERS);
      const existingQuery = query(
        membersRef,
        where('leadId', '==', request.leadId),
        where('memberUid', '==', request.memberUid)
      );

      const existingSnapshot = await getDocs(existingQuery);

      if (!existingSnapshot.empty) {
        // Reuse existing record - just update it to active
        const existingDoc = existingSnapshot.docs[0];
        const existingData = existingDoc.data() as MembershipDocument;

        const updateData: any = {
          status: 'active',
          addedBy: request.addedBy,
          addedAt: new Date().toISOString()
        };

        // Remove the removedAt and removedBy fields when reactivating
        if (existingData.removedAt) {
          updateData.removedAt = null;
        }
        if (existingData.removedBy) {
          updateData.removedBy = null;
        }

        await updateDoc(doc(db, COLLECTION_NAMES.MEMBERS, existingDoc.id), updateData);

        const leadMember: LeadMember = {
          uid: request.memberUid,
          displayName: existingData.memberDisplayName,
          email: existingData.memberEmail,
          photoURL: existingData.memberPhotoURL,
          username: existingData.memberUsername,
          addedBy: request.addedBy,
          addedAt: new Date().toISOString(),
          membershipId: existingDoc.id
        };

        // Clear cache to force refresh
        this.clearLeadCache(request.leadId);

        console.log(`✅ Reactivated existing member ${existingData.memberEmail} for lead ${request.leadId}`);
        return leadMember;
      }

      // No existing record - create new one
      const userDoc = await getDoc(doc(db, COLLECTION_NAMES.USERS, request.memberUid));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data() as User;

      const membershipDoc: Omit<MembershipDocument, 'id'> = {
        leadId: request.leadId,
        memberUid: request.memberUid,
        addedBy: request.addedBy,
        addedAt: new Date().toISOString(),
        status: 'active',
        memberDisplayName: userData.displayName || userData.email,
        memberEmail: userData.email,
        memberPhotoURL: userData.photoURL || undefined,
        memberUsername: userData.username
      };

      // Add to members collection
      const docRef = await addDoc(collection(db, COLLECTION_NAMES.MEMBERS), membershipDoc);

      // Create LeadMember for return
      const leadMember: LeadMember = {
        uid: request.memberUid,
        displayName: membershipDoc.memberDisplayName,
        email: membershipDoc.memberEmail,
        photoURL: membershipDoc.memberPhotoURL,
        username: membershipDoc.memberUsername,
        addedBy: request.addedBy,
        addedAt: membershipDoc.addedAt,
        membershipId: docRef.id
      };

      // Clear cache to force refresh
      this.clearLeadCache(request.leadId);

      console.log(`✅ Added new member ${userData.email} to lead ${request.leadId}`);
      return leadMember;
    } catch (error) {
      console.error('❌ Error adding member to lead:', error);
      throw new Error('Failed to add member to lead');
    }
  }

  /**
   * Remove a member from a lead
   */
  async removeMemberFromLead(request: RemoveMemberRequest): Promise<void> {
    try {
      // Find the membership document
      const membersRef = collection(db, COLLECTION_NAMES.MEMBERS);
      const q = query(
        membersRef,
        where('leadId', '==', request.leadId),
        where('memberUid', '==', request.memberUid),
        where('status', '==', 'active')
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Active membership not found');
      }

      const membershipDoc = querySnapshot.docs[0];
      const membershipId = membershipDoc.id;

      // Update status to removed instead of deleting
      await updateDoc(doc(db, COLLECTION_NAMES.MEMBERS, membershipId), {
        status: 'removed',
        removedAt: new Date().toISOString(),
        removedBy: request.removedBy
      });

      // Clear cache to force refresh
      this.clearLeadCache(request.leadId);

      console.log(`✅ Removed member ${request.memberUid} from lead ${request.leadId}`);
    } catch (error) {
      console.error('❌ Error removing member from lead:', error);
      throw new Error('Failed to remove member from lead');
    }
  }

  /**
   * Get all active members for a lead
   */
  async getLeadMembers(leadId: string, useCache: boolean = true): Promise<LeadMember[]> {
    try {
      // Check cache first
      if (useCache && this.membersCache.has(leadId)) {
        return this.membersCache.get(leadId)!;
      }

      const membersRef = collection(db, COLLECTION_NAMES.MEMBERS);
      const q = query(
        membersRef,
        where('leadId', '==', leadId),
        where('status', '==', 'active'),
        orderBy('addedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const members: LeadMember[] = [];

      querySnapshot.forEach(doc => {
        const data = doc.data() as MembershipDocument;
        members.push({
          uid: data.memberUid,
          displayName: data.memberDisplayName,
          email: data.memberEmail,
          photoURL: data.memberPhotoURL,
          username: data.memberUsername,
          addedBy: data.addedBy,
          addedAt: data.addedAt,
          membershipId: doc.id
        });
      });

      // Update cache
      this.membersCache.set(leadId, members);

      return members;
    } catch (error) {
      console.error('❌ Error getting lead members:', error);
      return [];
    }
  }

  /**
   * Check if a user is a member of a lead
   */
  async isMemberOfLead(leadId: string, userUid: string): Promise<boolean> {
    try {
      const members = await this.getLeadMembers(leadId, false); // force refresh
      const isMember = members.some(member => member.uid === userUid);
      console.log(`🔍 User ${userUid} is ${isMember ? 'already' : 'not'} a member of lead ${leadId}`);
      return isMember;
    } catch (error) {
      console.error('❌ Error checking member status:', error);
      return false;
    }
  }

  /**
   * Get recent users (top 5 by lastLoginAt)
   */
  async getRecentUsers(excludeUids: string[] = []): Promise<RecentUser[]> {
    try {
      // Check cache first
      if (this.recentUsersCache && Date.now() - this.lastRecentUsersFetch < this.CACHE_DURATION) {
        return this.recentUsersCache.filter(user => !excludeUids.includes(user.uid));
      }

      console.log('🔍 Loading recent users from Firestore...');

      const usersRef = collection(db, COLLECTION_NAMES.USERS);
      const q = query(
        usersRef,
        orderBy('lastLoginAt', 'desc'),
        limit(10) // Get more than we need to account for exclusions
      );

      const snapshot = await getDocs(q);
      const users: RecentUser[] = [];

      snapshot.forEach(doc => {
        const userData = doc.data() as User;
        if (userData.lastLoginAt) {
          users.push({
            uid: userData.uid,
            displayName: userData.displayName || userData.email,
            email: userData.email,
            photoURL: userData.photoURL || undefined,
            username: userData.username,
            lastLoginAt: userData.lastLoginAt
          });
        }
      });

      // Update cache
      this.recentUsersCache = users;
      this.lastRecentUsersFetch = Date.now();

      // Return filtered results (top 5 excluding specified UIDs)
      return users
        .filter(user => !excludeUids.includes(user.uid))
        .slice(0, 5);
    } catch (error) {
      console.error('❌ Error getting recent users:', error);
      return [];
    }
  }

  /**
   * Search for users to add as members
   */
  async searchUsers(searchTerm: string, excludeUids: string[] = []): Promise<MemberSearchResult[]> {
    try {
      console.log(`🔍 Searching users: "${searchTerm}"`);

      // Get all users (in production, you'd want better search/indexing)
      const usersRef = collection(db, COLLECTION_NAMES.USERS);
      const snapshot = await getDocs(usersRef);

      const users: MemberSearchResult[] = [];
      snapshot.forEach(doc => {
        const userData = doc.data() as User;

        // Skip excluded users
        if (excludeUids.includes(userData.uid)) {
          return;
        }

        // Filter by search term (email, display name, or username)
        const searchLower = searchTerm.toLowerCase();
        const matchesEmail = userData.email.toLowerCase().includes(searchLower);
        const matchesDisplayName = userData.displayName?.toLowerCase().includes(searchLower);
        const matchesUsername = userData.username?.toLowerCase().includes(searchLower);

        if (matchesEmail || matchesDisplayName || matchesUsername) {
          users.push({
            uid: userData.uid,
            displayName: userData.displayName || userData.email,
            email: userData.email,
            photoURL: userData.photoURL || undefined,
            username: userData.username,
            lastLoginAt: userData.lastLoginAt
          });
        }
      });

      // Sort by display name
      users.sort((a, b) => a.displayName.localeCompare(b.displayName));

      console.log(`✅ Found ${users.length} users matching "${searchTerm}"`);
      return users.slice(0, 10); // Limit to 10 results
    } catch (error) {
      console.error('❌ Error searching users:', error);
      throw new Error('Failed to search users');
    }
  }

  /**
   * Get all lead IDs that have at least one active member assigned.
   * Also populates the per-lead members cache as a side-effect so
   * MemberManager components can read from cache on mount.
   */
  async getAllLeadsWithMembers(): Promise<string[]> {
    try {
      // Return cached result if still fresh
      if (this.allLeadsWithMembersCache && Date.now() - this.allLeadsWithMembersFetchedAt < this.CACHE_DURATION) {
        return this.allLeadsWithMembersCache;
      }

      const membersRef = collection(db, COLLECTION_NAMES.MEMBERS);
      const q = query(membersRef, where('status', '==', 'active'));
      const querySnapshot = await getDocs(q);

      const byLead = new Map<string, LeadMember[]>();
      querySnapshot.forEach(doc => {
        const data = doc.data() as MembershipDocument;
        if (!data.leadId) return;
        const member: LeadMember = {
          uid: data.memberUid,
          displayName: data.memberDisplayName,
          email: data.memberEmail,
          photoURL: data.memberPhotoURL,
          username: data.memberUsername,
          addedBy: data.addedBy,
          addedAt: data.addedAt,
          membershipId: doc.id
        };
        const arr = byLead.get(data.leadId) || [];
        arr.push(member);
        byLead.set(data.leadId, arr);
      });

      // Populate cache so per-card loads hit cache instead of Firestore
      byLead.forEach((members, leadId) => {
        this.membersCache.set(leadId, members);
      });

      const result = Array.from(byLead.keys());
      this.allLeadsWithMembersCache = result;
      this.allLeadsWithMembersFetchedAt = Date.now();
      return result;
    } catch (error) {
      console.error('❌ Error getting all leads with members:', error);
      return [];
    }
  }

  /**
   * Get leads where user is a member
   */
  async getLeadsForMember(userUid: string): Promise<string[]> {
    try {
      // Return cached result if still fresh
      const cached = this.leadsForMemberCache.get(userUid);
      if (cached && Date.now() - cached.fetchedAt < this.CACHE_DURATION) {
        return cached.data;
      }

      const membersRef = collection(db, COLLECTION_NAMES.MEMBERS);
      const q = query(
        membersRef,
        where('memberUid', '==', userUid),
        where('status', '==', 'active')
      );

      const querySnapshot = await getDocs(q);
      const leadIds: string[] = [];

      querySnapshot.forEach(doc => {
        const data = doc.data() as MembershipDocument;
        leadIds.push(data.leadId);
      });

      this.leadsForMemberCache.set(userUid, { data: leadIds, fetchedAt: Date.now() });
      return leadIds;
    } catch (error) {
      console.error('❌ Error getting leads for member:', error);
      return [];
    }
  }

  /**
   * Update member cache helper
   */
  private updateMemberCache(leadId: string, member: LeadMember, action: 'add' | 'remove'): void {
    if (this.membersCache.has(leadId)) {
      const currentMembers = this.membersCache.get(leadId)!;

      if (action === 'add') {
        this.membersCache.set(leadId, [...currentMembers, member]);
      } else {
        this.membersCache.set(leadId, currentMembers.filter(m => m.uid !== member.uid));
      }
    }
    // Invalidate aggregate caches so they re-fetch on next access
    this.allLeadsWithMembersCache = null;
    this.leadsForMemberCache.clear();
  }

  /**
   * Clear cache for a specific lead
   */
  clearLeadCache(leadId: string): void {
    this.membersCache.delete(leadId);
  }

  /**
   * Clear all caches
   */
  clearAllCaches(): void {
    this.membersCache.clear();
    this.recentUsersCache = null;
    this.lastRecentUsersFetch = 0;
    this.allLeadsWithMembersCache = null;
    this.allLeadsWithMembersFetchedAt = 0;
    this.leadsForMemberCache.clear();
  }

  /**
   * Bulk load members for multiple leads (for optimization)
   */
  async bulkLoadLeadMembers(leadIds: string[]): Promise<Map<string, LeadMember[]>> {
    try {
      if (leadIds.length === 0) return new Map();

      const membersRef = collection(db, COLLECTION_NAMES.MEMBERS);
      const q = query(
        membersRef,
        where('leadId', 'in', leadIds.slice(0, 10)), // Firestore 'in' limit is 10
        where('status', '==', 'active'),
        orderBy('addedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const membersByLead = new Map<string, LeadMember[]>();

      // Initialize all lead IDs with empty arrays
      leadIds.forEach(leadId => membersByLead.set(leadId, []));

      querySnapshot.forEach(doc => {
        const data = doc.data() as MembershipDocument;
        const member: LeadMember = {
          uid: data.memberUid,
          displayName: data.memberDisplayName,
          email: data.memberEmail,
          photoURL: data.memberPhotoURL,
          username: data.memberUsername,
          addedBy: data.addedBy,
          addedAt: data.addedAt,
          membershipId: doc.id
        };

        const leadMembers = membersByLead.get(data.leadId) || [];
        leadMembers.push(member);
        membersByLead.set(data.leadId, leadMembers);
      });

      // Update cache
      membersByLead.forEach((members, leadId) => {
        this.membersCache.set(leadId, members);
      });

      return membersByLead;
    } catch (error) {
      console.error('❌ Error bulk loading lead members:', error);
      return new Map();
    }
  }
}

// Export singleton instance
export const memberService = MemberService.getInstance();